// ============================================================================
// view.pastureMap — the grazing cockpit owns every read it renders
// ============================================================================
// /pasture-map is the unusual case in this migration: almost nothing here was
// ever App's. Four of its five reads were already page-local — the mount bundle
// and the two lazily-loaded record histories were effects PastureMapView owned
// itself, calling the pasture SECDEF wrappers directly. What legacy.all claimed
// about them was simply never true; the parity default has one shape and the
// page has another.
//
// So this change is TWO different things, and they should be read separately.
//
// FIRST, THE ONE REAL HAND-OFF. The planner roster's four inputs arrived through
// three App providers at once: `breeders` and `feederGroups` off PigContext
// (the ppp-breeders-v1 / ppp-feeders-v1 blobs from the boot app_store
// transaction), `cattleForHome` off CattleHomeContext and `sheepForHome` off
// SheepHomeContext (the boot's two lightweight eight-column directories).
// pasture.planner.roster reads all four route-locally, which is what lets
// `pastureMap` retire from the frozen app_store consumer list in
// tests/fixtures/former-eager-dependencies.json. computePlannerGroupRoster
// itself did not move: a pure derivation is not a read.
//
// No new permission surface comes with it, and that is checked rather than
// assumed: home.light.planner already reads ppp-feeders-v1 and ppp-breeders-v1
// by key, and home.light.missed.inputs already reads the same eight cattle and
// sheep columns, both under a Light user's own scope. The pasture roster asks
// the same client for the same rows the same way — what changed is which module
// asks, not what the server is willing to answer.
//
// SECOND, THE OWNERSHIP OF READS THAT WERE ALREADY HERE. pasture.map.core,
// pasture.area.history and pasture.group.history take the scope key, the role
// gate, cancellation and the post-write refetch out of three hand-written
// effects. The RPCs are unchanged and are NOT re-issued in the catalog: every
// pasture table is deny-all RLS and the SECDEF wrappers are the only client
// door, so the catalog CALLS them with the injected client and Query's
// AbortSignal. Re-writing `sb.rpc('list_land_areas', …)` in the catalog would
// have given a read that already has exactly one physical call site a second
// one — the duplication the frozen render-read-site partition exists to catch.
//
// THAT IS WHY src/lib/pastureMapReads EXISTS, and the split is structural
// rather than tidiness. pastureMapApi binds the live browser client at module
// scope and src/lib/supabase reads `window.localStorage` while it initializes,
// so importing it from routeDataCatalog makes the whole surface manifest
// unloadable outside a browser — which is where every static guard that joins
// the manifest to the catalog reads it. The seven READ wrappers therefore moved
// to a module that takes the client as its first parameter, the convention
// supabaseTypes.ts describes for nearly every other module in src/lib; the
// WRITES stayed behind on pastureMapApi's own client, so an injected client can
// never be handed a mutation. The frozen call-site inventory records that as
// seven retirements and seven additions with IDENTICAL fingerprints, which is
// the evidence that only the file moved.
//
// WHY THE RESIDUAL LIST IS EMPTY, AS A CLAIM RATHER THAN AN OMISSION.
//
// After this change PastureMapView calls no provider hook but useAuth, which is
// identity rather than boot data and is on every authenticated surface. There is
// no App-owned write on this page either: every pasture mutation goes through
// the same pastureMapApi SECDEF wrappers the page has always called, so there is
// no persist* prop and no context mirror to keep in step — which is precisely
// the residual that keeps view.layers, view.breeding and view.sows `partial`.
//
// `legacy.boot.dataLoaded` is deliberately absent for the reason
// view.animalHistory records: it names a surface whose CONTENT depends on the
// global boot, not the fact of sitting behind the gate — which every unreleased
// surface still does, this one included. THIS DECLARATION MAKES NO BOOT-RELEASE
// CLAIM; releasing /pasture-map is a separate change with its own resolver
// fence, renderer branch and browser proof.
//
// TWO BEHAVIOURS ARE STATED RATHER THAN INHERITED, because a pure query cannot
// own either and both are load-bearing in the field:
//
//   * THE READ STAYS GATED ON THE OFFLINE QUEUE REPLAY. reload() replayed queued
//     operations BEFORE fetching, so the first paint showed work this device had
//     already done. pasture.map.core is parked (`enabled: false`) until that
//     replay resolves, so there is still exactly one fetch per mount and it
//     still happens after the replay.
//   * THE SAVED FIELD MAP STILL WINS ON FAILURE. The snapshot fallback and the
//     snapshot write are localStorage, not server reads, and they stayed in the
//     view. A failed read now reaches them through the resource's error instead
//     of a catch clause; a failed queue replay reaches the same helper directly,
//     exactly as it did when both were one try/catch.
// ============================================================================

import type {MigratedSurfaceDeclaration} from '../surfaceDataManifest';

const declaration: MigratedSurfaceDeclaration = Object.freeze({
  surface: 'view.pastureMap',
  obligations: Object.freeze([
    Object.freeze({id: 'pasture.map.core', owner: 'permanent'}),
    Object.freeze({id: 'pasture.area.history', owner: 'permanent'}),
    Object.freeze({id: 'pasture.group.history', owner: 'permanent'}),
    Object.freeze({id: 'pasture.planner.roster', owner: 'permanent'}),
  ]),
  residualLegacy: Object.freeze([] as const),
});

export default declaration;
